/* Role presets (docs/decisions/role-presets.md): starting column layouts per
   job role, per dealership. A preset is a starting point, never a lock, and
   never changes access. */
import React, {
    useCallback, useEffect, useId, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { Button, ConfirmDialog, Dialog } from '../ui';
import { CONFIRM_GUARD_MS } from '../shared/CancelTaskConfirm';
import { setLeaveGuard } from '../../utils/leaveGuard';
import useRolePresetsAdmin, { NEW_ID } from '../../hooks/useRolePresetsAdmin';
import { useDealershipSettings } from '../../contexts/settingsContext';
import { formatSettingTime } from '../../utils/dealershipSettings';
import { DEFAULT_COLUMN_LAYOUT, isDefaultLayout, normalizeColumnLayout } from '../../utils/inventoryColumns';
import {
    PRESET_DESCRIPTION_MAX, PRESET_NAME_MAX, deleteConfirmText, deleteImpact, deleteToastText, describeHistory, presetSummary,
} from '../../utils/rolePresets';
import ColumnLayoutList from '../InventoryV2/ColumnLayoutList';
import {
    Alert, Avatar, Note, SectionHead, Spinner,
} from './bits';
import { LockIcon, PlusIcon } from './icons';
import '../InventoryV2/InventoryV2.css';

const plural = (n, one, many) => `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`;

/** "Assign users…": tick the members this preset is assigned to. */
export function AssignUsersDialog({
    preset, members, onClose, onApply, busy,
}) {
    const [checked, setChecked] = useState(() => new Set(members.filter((u) => u.preset_id === preset.id).map((u) => u.id)));
    const before = useMemo(() => new Set(members.filter((u) => u.preset_id === preset.id).map((u) => u.id)), [members, preset.id]);
    const add = [...checked].filter((id) => !before.has(id));
    const drop = [...before].filter((id) => !checked.has(id));
    const toggle = (id, on) => setChecked((prev) => {
        const next = new Set(prev);
        if (on) next.add(id); else next.delete(id);
        return next;
    });
    return (
        <Dialog
            open
            onClose={onClose}
            title={`Assign users to ${preset.name}`}
            footer={(
                <>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button variant="primary" disabled={busy || (add.length + drop.length === 0)} onClick={() => onApply({ add, drop })}>
                        {busy ? <><Spinner />Saving…</> : 'Apply'}
                    </Button>
                </>
            )}
        >
            <p className="ad2-sub">
                Tick the members to assign this preset to. Unticking a member removes this assignment, so they start
                from the system default. Members assigned to another preset keep it unless you tick them. A member&apos;s
                own pick of a starting layout always wins.
            </p>
            {members.length === 0 ? (
                <div className="ad2-empty"><p>No members at this dealership yet.</p></div>
            ) : (
                <ul className="ad2-pre-assign" aria-label="Members">
                    {members.map((u) => {
                        const s = presetSummary(u);
                        const notes = [];
                        if (s.assigned && u.preset_id !== preset.id) notes.push(`assigned: ${s.assigned}`);
                        if (u.preset_choice_name) notes.push(`user chose: ${u.preset_choice_name}`);
                        return (
                            <li key={u.id}>
                                <label className="ad2-chk">
                                    <input
                                        type="checkbox"
                                        checked={checked.has(u.id)}
                                        onChange={(e) => toggle(u.id, e.target.checked)}
                                    />
                                    <span>
                                        <b>{u.display_name}</b>
                                        <span className="ad2-em">{u.email}</span>
                                        {notes.length > 0 && <span className="ad2-pre-note">{notes.join(' · ')}</span>}
                                    </span>
                                </label>
                            </li>
                        );
                    })}
                </ul>
            )}
        </Dialog>
    );
}

AssignUsersDialog.propTypes = {
    preset: PropTypes.object.isRequired,
    members: PropTypes.array.isRequired,
    onClose: PropTypes.func.isRequired,
    onApply: PropTypes.func.isRequired,
    busy: PropTypes.bool,
};

function PresetHistory({ history }) {
    if (!history) return null;
    if (history.loading) return <div className="ad2-loading" role="status"><Spinner /> Loading history…</div>;
    if (history.error) return <Alert>{history.error}</Alert>;
    if (!history.rows.length) return <div className="ad2-empty"><p>No changes recorded yet.</p></div>;
    return (
        <div className="v2-tscroll">
            <table className="v2-table ad2-pre-hist" aria-label="Preset history">
                <thead>
                    <tr>
                        <th scope="col"><span>When</span></th>
                        <th scope="col"><span>Who</span></th>
                        <th scope="col"><span>Action</span></th>
                        <th scope="col"><span>Change</span></th>
                    </tr>
                </thead>
                <tbody>
                    {history.rows.map((h, i) => {
                        const d = describeHistory(h);
                        return (
                            // eslint-disable-next-line react/no-array-index-key
                            <tr key={`${h.changed_at}-${i}`}>
                                {/* Fresh QA F-5: when and action never break; who and change wrap. */}
                                <td className="ad2-pre-hist-fixed">{formatSettingTime(h.changed_at)}</td>
                                <td className="ad2-pre-hist-wrap">{h.changed_by ? <span title={h.changed_by}>{h.changed_by}</span> : '—'}</td>
                                <td className="ad2-pre-hist-fixed">{d.action}</td>
                                <td className="ad2-pre-hist-wrap">{d.what}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
PresetHistory.propTypes = { history: PropTypes.object };

/**
 * The admin section. `dealers`: the dealerships the caller may pick (super
 * admins pick any; a dealer admin gets their own).
 */
export default function PresetsSection({
    dealers, isSuper, onToast, onUsersChanged,
}) {
    const baseId = `ad2pre${useId().replace(/:/g, '')}`;
    const current = useDealershipSettings();
    const firstId = dealers.some((d) => d.id === current.orgId) ? current.orgId : (dealers[0]?.id || current.orgId || null);
    const [orgId, setOrgId] = useState(firstId);
    useEffect(() => {
        if (!orgId && firstId) setOrgId(firstId);
    }, [orgId, firstId]);

    const p = useRolePresetsAdmin(orgId, { onUsersChanged });
    const readOnly = !p.canEdit;
    const toast = (text) => { if (onToast) onToast(text); };
    const [announcement, setAnnouncement] = useState('');
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [assignOpen, setAssignOpen] = useState(false);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [confirmReload, setConfirmReload] = useState(false);
    // QA P-6: "Enter a name." waits for a blur, an edit or a save attempt.
    const [nameTouched, setNameTouched] = useState(false);
    const confirmedAt = useRef(0);
    const deleteYesRef = useRef(null);
    const alertRef = useRef(null);
    const nameRef = useRef(null);
    const titleRef = useRef(null);
    const listRef = useRef(null);
    const newRef = useRef(null);
    // Fresh QA F-6: after a delete, focus the preset now shown (or "New preset").
    const [focusAfterDelete, setFocusAfterDelete] = useState(null);
    useEffect(() => {
        if (!focusAfterDelete) return;
        setFocusAfterDelete(null);
        const btn = p.selectedId
            ? [...(listRef.current?.querySelectorAll('button[data-preset-id]') || [])].find((b) => b.dataset.presetId === p.selectedId)
            : null;
        (btn || newRef.current || titleRef.current)?.focus();
    }, [focusAfterDelete, p.selectedId]);

    useEffect(() => {
        setConfirmDelete(false);
        setHistoryOpen(false);
        setConfirmReload(false);
        setAnnouncement(''); // QA P-7: no stale column message for another preset.
        setNameTouched(false); // QA P-6
    }, [p.selectedId, orgId]);
    useEffect(() => {
        if (confirmDelete) { confirmedAt.current = Date.now(); deleteYesRef.current?.focus(); }
    }, [confirmDelete]);
    useEffect(() => {
        if (p.saveError) alertRef.current?.focus();
    }, [p.saveError]);
    useEffect(() => {
        if (p.isNew) nameRef.current?.focus();
    }, [p.isNew]);

    // Unsaved edits: leaving the section, the console, the preset or the
    // dealership asks in the page first (QA P2-6 pattern).
    const [leaveAsk, setLeaveAsk] = useState(null);
    const askLeave = useCallback(() => new Promise((resolve) => { setLeaveAsk({ resolve }); }), []);
    const guarded = p.loaded && p.dirty && !readOnly;
    useEffect(() => (guarded ? setLeaveGuard(askLeave) : undefined), [guarded, askLeave]);
    useEffect(() => {
        if (!guarded) return undefined;
        const onUnload = (e) => { e.preventDefault(); e.returnValue = ''; };
        window.addEventListener('beforeunload', onUnload);
        return () => window.removeEventListener('beforeunload', onUnload);
    }, [guarded]);
    const answerLeave = (leave) => {
        const ask = leaveAsk;
        setLeaveAsk(null);
        ask?.resolve(leave);
    };
    const pickDealer = async (next) => {
        if (guarded && !(await askLeave())) return;
        setOrgId(next);
    };
    const pickPreset = async (id) => {
        if (id === p.selectedId) return;
        if (guarded && !(await askLeave())) return;
        p.select(id);
    };

    const submit = async (e) => {
        e.preventDefault();
        setNameTouched(true);
        if (!p.valid) { nameRef.current?.focus(); return; }
        const creating = p.isNew;
        const saved = await p.save();
        if (saved) {
            setAnnouncement('');
            toast(creating ? `${saved.name || 'Preset'} created` : 'Preset saved');
        }
    };
    const doReload = async () => {
        setConfirmReload(false);
        await p.reload();
        titleRef.current?.focus();
    };
    const askReload = () => {
        if (p.dirty && !p.isNew) setConfirmReload(true);
        else doReload();
    };
    const duplicate = async () => {
        if (guarded && !(await askLeave())) return;
        const created = await p.duplicate();
        if (created) toast(`${created.name || 'Copy'} created`);
    };
    const doDelete = async (e) => {
        // The 400 ms / double-click guard of every v2 confirm.
        if (e.detail > 1 || Date.now() - confirmedAt.current < CONFIRM_GUARD_MS) return;
        const name = p.selected?.name;
        // The preset after it in the list (else the one before) is shown next.
        const at = p.items.findIndex((it) => it.id === p.selectedId);
        const nextId = (p.items[at + 1] || p.items[at - 1] || null)?.id || null;
        const res = await p.remove(nextId);
        setConfirmDelete(false);
        if (!res) return;
        setFocusAfterDelete(Date.now());
        toast(deleteToastText(name, res));
    };
    const restore = async () => {
        const n = await p.restoreDefaults();
        if (n === null) return;
        toast(n ? `${plural(n, 'default preset', 'default presets')} restored` : 'Every default preset is already there');
    };
    const applyAssign = async (change) => {
        const ok = await p.assign(change);
        if (ok) {
            setAssignOpen(false);
            toast('Assignments updated');
        }
    };

    const dealerName = dealers.find((d) => d.id === orgId)?.name;
    const sel = p.selected;
    const assignedUsers = sel ? p.membersOf(sel.id) : [];
    const impact = deleteImpact(sel, p.members, p.items);
    const resetDisabled = readOnly || isDefaultLayout(p.draft.layout);
    const nameError = (nameTouched || String(p.draft.name || '').trim() !== '') ? p.errors.name : null;

    return (
        <>
            <SectionHead
                eyebrow={dealerName || 'Dealership'}
                title="Role presets"
                titleRef={titleRef}
                actions={!readOnly && p.loaded ? (
                    <>
                        <Button variant="ghost" onClick={restore} disabled={p.busy}>Restore default presets</Button>
                        <Button ref={newRef} variant="primary" onClick={() => pickPreset(NEW_ID)} disabled={p.busy}><PlusIcon />New preset</Button>
                    </>
                ) : null}
            >
                Starting column layouts for each job role at this dealership. A preset is a starting point: people can
                change their own layout and reset back to it. Presets never change what anyone may see or do.
            </SectionHead>

            {isSuper && dealers.length > 0 && (
                <label className="ad2-fld ad2-set-pick" htmlFor={`${baseId}-org`}>
                    <span>Dealership</span>
                    <select id={`${baseId}-org`} className="v2-input" value={orgId || ''} onChange={(e) => pickDealer(e.target.value)}>
                        {dealers.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                </label>
            )}

            {!orgId && <Note tone="warn">No dealership to configure.</Note>}
            {p.loadError && <Alert>{p.loadError}</Alert>}
            {p.loading && !p.loaded && <div className="ad2-loading" role="status"><Spinner /> Loading role presets…</div>}

            {p.loaded && readOnly && (
                <Note><LockIcon /> You can view these presets. Only an admin of this dealership or Fusable staff can change them.</Note>
            )}

            {p.loaded && (
                <div className="ad2-pre">
                    <nav className="v2-card ad2-pre-side" aria-label="Role presets" ref={listRef}>
                        <h2 className="ad2-pre-h">Job roles</h2>
                        {p.items.length === 0 && !p.isNew ? (
                            <p className="ad2-hint">No presets yet.</p>
                        ) : (
                            <ul>
                                {p.items.map((it) => (
                                    <li key={it.id}>
                                        <button
                                            type="button"
                                            data-preset-id={it.id}
                                            aria-current={p.selectedId === it.id ? 'true' : undefined}
                                            onClick={() => pickPreset(it.id)}
                                        >
                                            <span className="ad2-pre-nm" title={it.name}>{it.name}</span>
                                            <span className="ad2-pre-n" aria-label={plural(it.assigned || 0, 'user', 'users')}>{(it.assigned || 0).toLocaleString('en-US')}</span>
                                        </button>
                                    </li>
                                ))}
                                {p.isNew && (
                                    <li><button type="button" aria-current="true"><span className="ad2-pre-nm">New preset</span><span /></button></li>
                                )}
                            </ul>
                        )}
                    </nav>

                    {(sel || p.isNew) ? (
                        <form className="v2-card ad2-pre-panel" onSubmit={submit} noValidate aria-labelledby={`${baseId}-h`}>
                            <div className="ad2-pre-head">
                                <div>
                                    <h2 id={`${baseId}-h`} className="ad2-h2">{p.isNew ? 'New preset' : sel.name}</h2>
                                    {sel && (
                                        <p className="ad2-hint">
                                            Version {sel.version}
                                            {sel.template_key ? ' · default preset' : ''}
                                            {sel.updated_at ? ` · updated ${formatSettingTime(sel.updated_at)}` : ''}
                                            {sel.updated_by ? ` by ${sel.updated_by}` : ''}
                                        </p>
                                    )}
                                </div>
                                {sel && (
                                    <div className="ad2-row">
                                        <Button
                                            size="sm"
                                            aria-expanded={historyOpen}
                                            onClick={() => { const next = !historyOpen; setHistoryOpen(next); if (next) p.loadHistory(); }}
                                        >
                                            History
                                        </Button>
                                        {!readOnly && <Button size="sm" onClick={duplicate} disabled={p.busy}>Duplicate</Button>}
                                        {!readOnly && (
                                            <Button size="sm" className="ad2-btn-danger" onClick={() => setConfirmDelete(true)} disabled={p.busy}>Delete</Button>
                                        )}
                                    </div>
                                )}
                            </div>

                            {sel && confirmDelete && (
                                <div className="ad2-inline-confirm" role="group" aria-label="Delete this preset?">
                                    <p>
                                        Delete <b>{sel.name}</b>?
                                        {' '}{deleteConfirmText(impact)}
                                    </p>
                                    <Button onClick={() => setConfirmDelete(false)}>Cancel</Button>
                                    <Button ref={deleteYesRef} variant="danger" disabled={p.busy} onClick={doDelete}>Delete</Button>
                                </div>
                            )}

                            {sel && (
                                <div className="ad2-stats ad2-pre-stats" role="group" aria-label="Who uses this preset">
                                    <div className="ad2-stat">
                                        <span className="ad2-stat-n">{(sel.assigned || 0).toLocaleString('en-US')}</span>
                                        <span className="ad2-stat-l">Assigned</span>
                                        <span className="ad2-hint">start from this preset</span>
                                    </div>
                                    <div className="ad2-stat">
                                        <span className="ad2-stat-n">{(sel.following || 0).toLocaleString('en-US')}</span>
                                        <span className="ad2-stat-l">Following</span>
                                        <span className="ad2-hint">get your changes right away</span>
                                    </div>
                                    <div className="ad2-stat">
                                        <span className="ad2-stat-n">{(sel.customized || 0).toLocaleString('en-US')}</span>
                                        <span className="ad2-stat-l">Customized</span>
                                        <span className="ad2-hint">keep their own layout and can reset to the preset</span>
                                    </div>
                                </div>
                            )}

                            {historyOpen && sel && (
                                <section className="ad2-pre-sec" aria-label="History">
                                    <h3 className="ad2-pre-sh">History</h3>
                                    <PresetHistory history={p.history} />
                                </section>
                            )}

                            <div className="ad2-two">
                                <div className={`ad2-fld${nameError ? ' is-invalid' : ''}`}>
                                    <label className="ad2-pre-lbl" htmlFor={`${baseId}-name`}>Name</label>
                                    <input
                                        id={`${baseId}-name`}
                                        ref={nameRef}
                                        className="v2-input"
                                        type="text"
                                        maxLength={PRESET_NAME_MAX + 10}
                                        value={p.draft.name}
                                        readOnly={readOnly}
                                        aria-invalid={nameError ? 'true' : undefined}
                                        aria-describedby={nameError ? `${baseId}-name-err` : undefined}
                                        onBlur={() => setNameTouched(true)}
                                        onChange={(e) => { setNameTouched(true); p.setField('name', e.target.value); }}
                                    />
                                    {nameError && <span id={`${baseId}-name-err`} className="ad2-set-err">{nameError}</span>}
                                </div>
                                <div className={`ad2-fld${p.errors.description ? ' is-invalid' : ''}`}>
                                    <label className="ad2-pre-lbl" htmlFor={`${baseId}-desc`}>Description</label>
                                    <input
                                        id={`${baseId}-desc`}
                                        className="v2-input"
                                        type="text"
                                        value={p.draft.description}
                                        readOnly={readOnly}
                                        aria-invalid={p.errors.description ? 'true' : undefined}
                                        aria-describedby={`${baseId}-desc-${p.errors.description ? 'err' : 'hint'}`}
                                        onChange={(e) => p.setField('description', e.target.value)}
                                    />
                                    {p.errors.description
                                        ? <span id={`${baseId}-desc-err`} className="ad2-set-err">{p.errors.description}</span>
                                        : <span id={`${baseId}-desc-hint`} className="ad2-hint">{`Up to ${PRESET_DESCRIPTION_MAX} characters.`}</span>}
                                </div>
                            </div>

                            <section className="ad2-pre-sec" aria-labelledby={`${baseId}-cols-h`}>
                                <div className="ad2-pre-sec-head">
                                    <h3 id={`${baseId}-cols-h`} className="ad2-pre-sh">Inventory columns</h3>
                                    {!readOnly && (
                                        <Button
                                            size="sm"
                                            disabled={resetDisabled}
                                            onClick={() => {
                                                p.setField('layout', normalizeColumnLayout(DEFAULT_COLUMN_LAYOUT));
                                                setAnnouncement('Columns reset to the system default.');
                                            }}
                                        >
                                            Reset to system default
                                        </Button>
                                    )}
                                </div>
                                <p className="ad2-hint">Show, hide and order the columns this role starts with. Stock # always comes first.</p>
                                <div className="ad2-pre-cols">
                                    <ColumnLayoutList
                                        layout={p.draft.layout}
                                        onChange={(l) => p.setField('layout', l)}
                                        onAnnounce={setAnnouncement}
                                        label="Preset column order"
                                        disabled={readOnly}
                                        idPrefix={`${baseId}-cols`}
                                    />
                                </div>
                                <p className="v2-sr" role="status" aria-live="polite">{announcement}</p>
                            </section>

                            {sel && (
                                <section className="ad2-pre-sec" aria-labelledby={`${baseId}-users-h`}>
                                    <div className="ad2-pre-sec-head">
                                        <h3 id={`${baseId}-users-h`} className="ad2-pre-sh">Users starting from it</h3>
                                        {!readOnly && <Button size="sm" onClick={() => setAssignOpen(true)} disabled={p.busy}>Assign users…</Button>}
                                    </div>
                                    {assignedUsers.length === 0 && impact.outside === 0 ? (
                                        <p className="ad2-hint">Nobody yet. Assign members, or members can pick it in My preferences.</p>
                                    ) : (
                                        <ul className="ad2-pre-users" aria-label="Users starting from this preset">
                                            {assignedUsers.map((u) => {
                                                const chosen = u.preset_id !== sel.id;
                                                return (
                                                    <li key={u.id}>
                                                        <Avatar user={u} kind="dealer" />
                                                        <span>
                                                            <b>{u.display_name}</b>
                                                            <span className="ad2-em">
                                                                {chosen ? `own pick${u.preset_name ? ` · assigned: ${u.preset_name}` : ''}` : 'assigned'}
                                                            </span>
                                                        </span>
                                                    </li>
                                                );
                                            })}
                                            {impact.outside > 0 && (
                                                // Fresh QA F-4: picks by users outside the dealership (e.g. vendor
                                                // staff) are in the Assigned count; the member list has no names for them.
                                                <li>
                                                    <span>
                                                        <b>{`${plural(impact.outside, 'user', 'users')} outside this dealership`}</b>
                                                        <span className="ad2-em">own pick</span>
                                                    </span>
                                                </li>
                                            )}
                                        </ul>
                                    )}
                                </section>
                            )}

                            {p.saveError && (
                                <div className="ad2-alert ad2-set-alert" role="alert" tabIndex={-1} ref={alertRef}>
                                    <span>{p.saveError}</span>
                                    {p.conflict && !confirmReload && <Button size="sm" onClick={askReload}>Reload</Button>}
                                    {p.conflict && confirmReload && (
                                        <span className="ad2-set-confirm" role="group" aria-label="Discard your unsaved changes?">
                                            <span>Discard your unsaved changes?</span>
                                            <Button size="sm" variant="danger" onClick={doReload}>Discard and reload</Button>
                                            <Button size="sm" onClick={() => setConfirmReload(false)}>Keep editing</Button>
                                        </span>
                                    )}
                                </div>
                            )}

                            {!readOnly && (
                                <div className="ad2-set-acts">
                                    <span className="ad2-hint">{p.dirty ? 'Unsaved changes' : 'No unsaved changes'}</span>
                                    <Button onClick={p.discard} disabled={(!p.dirty && !p.conflict) || p.saving}>{p.isNew ? 'Cancel' : 'Discard'}</Button>
                                    <Button variant="primary" type="submit" disabled={!p.dirty || p.saving}>
                                        {p.saving ? <><Spinner />Saving…</> : (p.isNew ? 'Create preset' : 'Save')}
                                    </Button>
                                </div>
                            )}
                        </form>
                    ) : (
                        <div className="v2-card ad2-pre-panel">
                            <div className="ad2-empty">
                                <p>No role presets at this dealership.</p>
                                {!readOnly && <Button onClick={restore} disabled={p.busy}>Restore default presets</Button>}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {assignOpen && sel && (
                <AssignUsersDialog
                    preset={sel}
                    members={p.members}
                    busy={p.busy}
                    onClose={() => setAssignOpen(false)}
                    onApply={applyAssign}
                />
            )}

            <ConfirmDialog
                open={Boolean(leaveAsk)}
                title="Discard unsaved changes?"
                message="You have unsaved changes to this role preset. Leaving discards them."
                cancelLabel="Keep editing"
                confirmLabel="Discard"
                danger
                onConfirm={() => answerLeave(true)}
                onCancel={() => answerLeave(false)}
            />
        </>
    );
}

PresetsSection.propTypes = {
    dealers: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, name: PropTypes.string })).isRequired,
    isSuper: PropTypes.bool,
    onToast: PropTypes.func,
    onUsersChanged: PropTypes.func,
};
